<script setup>
/**
 * SzInAppNotifier — toaster sonner-like pra notificações in-app de novas
 * mensagens em conversas atribuídas ao agente atual.
 *
 * Critérios pra disparar:
 *   - msg.message_type === 0 (incoming)
 *   - conversa.assignee.id === currentUser.id (responsável direto)
 *   - NÃO disparar se conversa ativa NO MOMENTO em que tab está visível
 *     (usuário já tá vendo a mensagem)
 *   - Setting `enable_in_app_notifications` no uiSettings habilitado
 *
 * UX:
 *   - Stack vertical canto inferior direito, máx 3 simultâneos
 *   - Auto-dismiss 5s
 *   - Agrupa mensagens da MESMA conversa (count "X novas mensagens de Y")
 *   - "Responder" abre input inline → envia + dismissa
 *   - Click no card (fora do botão) navega pra conversa
 *   - X dismissa manual
 *
 * Não toca som (projeto já tem audio notifications via AudioNotifications.vue).
 */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';

const store = useStore();
const router = useRouter();
const { uiSettings } = useUISettings();

const MAX_STACK = 3;
const AUTO_DISMISS_MS = 5000;

const notifications = ref([]);
const replyingId = ref(null);
const replyText = ref('');
const replySending = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);
const selectedChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const enabled = computed(() => {
  const v = uiSettings.value?.enable_in_app_notifications;
  // Default: ON. Só desliga se explicitamente false.
  return v === false ? false : true;
});

function isUserViewingConversation(conversationId) {
  if (!selectedChat.value) return false;
  if (selectedChat.value.id !== conversationId) return false;
  // Vê só se tab tá visível. Tab em background → toaster fica visível pra
  // quando voltar (cenário 2.B confirmado pelo operador).
  return document.visibilityState === 'visible';
}

function shouldShow(message) {
  if (!enabled.value) return false;
  if (!currentUser.value) return false;
  if (Number(message?.message_type) !== 0) return false;

  const conv =
    message?.conversation ||
    store.getters.getConversationById(message.conversation_id);
  if (!conv) return false;

  const assigneeId =
    conv?.meta?.assignee?.id ?? conv?.assignee?.id ?? null;
  if (assigneeId !== currentUser.value.id) return false;

  if (isUserViewingConversation(message.conversation_id)) return false;

  return true;
}

function attachmentPreview(message) {
  const atts = message?.attachments || [];
  if (!atts.length) return '';
  const t = atts[0]?.file_type || atts[0]?.type || 'arquivo';
  const map = {
    image: '🖼️ Imagem',
    video: '🎥 Vídeo',
    audio: '🎵 Áudio',
    file: '📎 Arquivo',
    location: '📍 Localização',
    contact: '👤 Contato',
  };
  return map[t] || '📎 Anexo';
}

function previewText(message) {
  const content = String(message?.content || '').trim();
  if (content) return content;
  return attachmentPreview(message) || 'Nova mensagem';
}

function senderInfo(message) {
  const conv =
    message?.conversation ||
    store.getters.getConversationById(message.conversation_id);
  const sender =
    message?.sender || conv?.meta?.sender || {};
  return {
    name: sender.name || sender.available_name || 'Contato',
    avatar: sender.thumbnail || sender.avatar_url || '',
  };
}

function senderInitials(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join('');
}

function dismiss(id) {
  const idx = notifications.value.findIndex(n => n.id === id);
  if (idx < 0) return;
  clearTimeout(notifications.value[idx].timer);
  notifications.value.splice(idx, 1);
  if (replyingId.value === id) {
    replyingId.value = null;
    replyText.value = '';
  }
}

function resetTimer(notif) {
  clearTimeout(notif.timer);
  notif.timer = setTimeout(() => dismiss(notif.id), AUTO_DISMISS_MS);
}

function addOrUpdate(message) {
  const convId = message.conversation_id;
  const existing = notifications.value.find(n => n.conversationId === convId);
  if (existing) {
    existing.count += 1;
    existing.content = previewText(message);
    existing.lastMessageId = message.id;
    resetTimer(existing);
    return;
  }
  if (notifications.value.length >= MAX_STACK) {
    const oldest = notifications.value[0];
    clearTimeout(oldest.timer);
    notifications.value.shift();
  }
  const info = senderInfo(message);
  const id = 'sz-iann-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  const notif = {
    id,
    conversationId: convId,
    senderName: info.name,
    avatar: info.avatar,
    content: previewText(message),
    count: 1,
    lastMessageId: message.id,
    timer: null,
  };
  notif.timer = setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
  notifications.value.push(notif);
}

function openConversation(notif) {
  const path =
    '/app/accounts/' + accountId.value + '/conversations/' + notif.conversationId;
  router.push(path).catch(() => { window.location.href = path; });
  dismiss(notif.id);
}

function startReply(notif) {
  replyingId.value = notif.id;
  replyText.value = '';
  // Pausa auto-dismiss enquanto digita
  clearTimeout(notif.timer);
}

function cancelReply(notif) {
  replyingId.value = null;
  replyText.value = '';
  resetTimer(notif);
}

async function sendQuickReply(notif) {
  const txt = replyText.value.trim();
  if (!txt || replySending.value) return;
  replySending.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: notif.conversationId,
      message: txt,
      private: false,
    });
    dismiss(notif.id);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SzInAppNotifier] reply error', e);
  }
  replySending.value = false;
}

// Hook na mutation ADD_MESSAGE (server-side push via ActionCable acaba aqui).
let unsubscribe = null;
onMounted(() => {
  unsubscribe = store.subscribe(mutation => {
    if (mutation.type !== 'ADD_MESSAGE') return;
    const message = mutation.payload;
    if (!shouldShow(message)) return;
    addOrUpdate(message);
  });
});

onUnmounted(() => {
  if (unsubscribe) unsubscribe();
  notifications.value.forEach(n => clearTimeout(n.timer));
  notifications.value = [];
});
</script>

<template>
  <Teleport to="body">
    <div class="sz-iann-stack" :aria-live="'polite'">
      <transition-group name="sz-iann">
        <div
          v-for="n in notifications"
          :key="n.id"
          class="sz-iann-card"
          :class="{ 'sz-iann-card--replying': replyingId === n.id }"
          @click="replyingId !== n.id && openConversation(n)"
        >
          <div class="sz-iann-avatar-wrap">
            <img v-if="n.avatar" :src="n.avatar" :alt="n.senderName" class="sz-iann-avatar" />
            <div v-else class="sz-iann-avatar sz-iann-avatar--initials">
              {{ senderInitials(n.senderName) }}
            </div>
          </div>

          <div class="sz-iann-body">
            <div class="sz-iann-head">
              <span class="sz-iann-name">{{ n.senderName }}</span>
              <span v-if="n.count > 1" class="sz-iann-count">
                {{ n.count }} novas
              </span>
            </div>
            <div v-if="replyingId !== n.id" class="sz-iann-content">{{ n.content }}</div>

            <div v-else class="sz-iann-reply" @click.stop>
              <input
                v-model="replyText"
                type="text"
                placeholder="Digite sua resposta…"
                class="sz-iann-input"
                :disabled="replySending"
                @keydown.enter.prevent="sendQuickReply(n)"
                @keydown.esc.prevent="cancelReply(n)"
              />
              <button
                type="button"
                class="sz-iann-send"
                :disabled="!replyText.trim() || replySending"
                @click="sendQuickReply(n)"
              >
                {{ replySending ? '…' : 'Enviar' }}
              </button>
              <button
                type="button"
                class="sz-iann-cancel"
                @click="cancelReply(n)"
              >
                Cancelar
              </button>
            </div>
          </div>

          <button
            v-if="replyingId !== n.id"
            type="button"
            class="sz-iann-action"
            title="Responder rapidamente"
            @click.stop="startReply(n)"
          >
            Responder
          </button>

          <button
            v-if="replyingId !== n.id"
            type="button"
            class="sz-iann-close"
            title="Dispensar"
            @click.stop="dismiss(n.id)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </Teleport>
</template>

<style scoped>
.sz-iann-stack {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  pointer-events: none;
  max-width: 380px;
  font-family: Inter, system-ui, sans-serif;
}
.sz-iann-card {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #2A2D31;
  color: #EDEEEF;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.06);
  min-height: 56px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.sz-iann-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
}
.sz-iann-card--replying { cursor: default; }
.sz-iann-card--replying:hover { transform: none; }

.sz-iann-avatar-wrap { flex-shrink: 0; }
.sz-iann-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: #3D3F43;
}
.sz-iann-avatar--initials {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #EDEEEF;
}

.sz-iann-body { flex: 1; min-width: 0; }
.sz-iann-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}
.sz-iann-name {
  font-size: 13px;
  font-weight: 600;
  color: #FAFAFA;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
.sz-iann-count {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #388E3C;
  color: #fff;
  flex-shrink: 0;
}
.sz-iann-content {
  font-size: 12px;
  color: #B8B9BB;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sz-iann-reply { display: flex; gap: 6px; align-items: center; margin-top: 4px; }
.sz-iann-input {
  flex: 1;
  background: #1C1E22;
  border: 1px solid #3D3F43;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  color: #FAFAFA;
  outline: none;
  min-width: 0;
}
.sz-iann-input:focus { border-color: #388E3C; }
.sz-iann-send,
.sz-iann-cancel {
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 0;
  cursor: pointer;
  white-space: nowrap;
}
.sz-iann-send {
  background: #388E3C;
  color: #fff;
}
.sz-iann-send:disabled {
  background: #3D3F43;
  color: #6B7280;
  cursor: not-allowed;
}
.sz-iann-cancel {
  background: transparent;
  color: #9CA3AF;
}
.sz-iann-cancel:hover { color: #FAFAFA; }

.sz-iann-action {
  flex-shrink: 0;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #EDEEEF;
  border: 0;
  cursor: pointer;
  white-space: nowrap;
}
.sz-iann-action:hover { background: rgba(255, 255, 255, 0.14); }

.sz-iann-close {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 0;
  background: rgba(255, 255, 255, 0.06);
  color: #9CA3AF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sz-iann-close:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #FAFAFA;
}

.sz-iann-enter-active,
.sz-iann-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.sz-iann-enter-from {
  opacity: 0;
  transform: translateX(24px);
}
.sz-iann-leave-to {
  opacity: 0;
  transform: translateX(24px);
}
</style>
